<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import commercialWidgetsAPI from 'dashboard/api/commercialWidgets';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import WidgetCharts from './WidgetCharts.vue';
import WidgetTable from './WidgetTable.vue';
import { formatWidgetValue } from './widgetFormatters';

const props = defineProps({
  widget: { type: Object, required: true },
  params: { type: Object, required: true },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();
const data = ref(null);
const isLoading = ref(true);
const hasError = ref(false);
const isMenuOpen = ref(false);

let latestRequest = 0;

// Só a resposta da última requisição vale (o período pode mudar rápido).
const fetchData = async () => {
  latestRequest += 1;
  const request = latestRequest;
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await commercialWidgetsAPI.getData(
      props.widget.id,
      props.params
    );
    if (request === latestRequest) data.value = response.data;
  } catch {
    if (request === latestRequest) hasError.value = true;
  } finally {
    if (request === latestRequest) isLoading.value = false;
  }
};

// Recarrega quando o período/filtros OU o próprio widget muda (editar troca a
// visualização: sem isso o cartão desenharia dados do formato antigo e quebraria).
// A chave do widget é um texto: o objeto é recriado a cada mover/redimensionar
// e observá-lo faria todos os cartões buscarem os dados de novo.
watch(() => props.params, fetchData, { immediate: true, deep: true });
watch(() => `${props.widget.id}:${props.widget.updated_at}`, fetchData);

const isInvalid = computed(() => data.value?.error === 'invalid_reference');
const isChart = computed(() =>
  ['bars', 'doughnut', 'line'].includes(props.widget.visualization)
);
const isGrouped = computed(() =>
  ['bars', 'doughnut', 'table'].includes(props.widget.visualization)
);

const menuItems = computed(() => [
  {
    label: t('COMMERCIAL_WIDGETS.MENU.EDIT'),
    action: 'edit',
    value: 'edit',
    icon: 'i-lucide-pencil',
  },
  {
    label: t('COMMERCIAL_WIDGETS.MENU.DELETE'),
    action: 'delete',
    value: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const onMenuAction = ({ action }) => {
  isMenuOpen.value = false;
  if (action === 'edit') emit('edit');
  else emit('delete');
};

// Regra de data (D11) explicada no tooltip do título.
const conditions = computed(() => props.widget.config.conditions || []);

const dateRuleKey = computed(() => {
  const { visualization, config } = props.widget;
  if (visualization === 'rate') return 'RATE';
  if (config.unit !== 'deal') return 'CONTACT_COMPANY';
  if (config.date_basis === 'entry') return 'ENTRY';
  const usesClosing =
    config.date_basis === 'closing' ||
    conditions.value.some(
      condition =>
        condition.source === 'stage_type' &&
        condition.values.some(value => ['won', 'lost'].includes(value))
    );
  return usesClosing ? 'CLOSING' : 'DEAL_CREATED';
});

const tooltip = computed(() => {
  const { config } = props.widget;
  const lines = [
    t(`COMMERCIAL_WIDGETS.TOOLTIPS.DATE_RULE.${dateRuleKey.value}`),
  ];
  if (conditions.value.some(condition => condition.source === 'pipeline')) {
    lines.push(t('COMMERCIAL_WIDGETS.TOOLTIPS.PIPELINE_INTERSECTION'));
  }
  if (isGrouped.value && ['label', 'field'].includes(config.group_by.source)) {
    lines.push(t('COMMERCIAL_WIDGETS.TOOLTIPS.MULTIPLE_VALUES'));
  }
  return lines.join(' ');
});
</script>

<template>
  <article
    class="flex flex-col gap-2 p-3 border rounded-lg h-full min-h-0 min-w-0 border-n-weak bg-n-solid-1"
  >
    <header class="flex items-start gap-1">
      <span
        data-pdf-ignore
        class="hidden mt-0.5 cursor-grab widget-drag-handle i-lucide-grip-vertical size-4 shrink-0 text-n-slate-10 lg:block"
        :title="t('COMMERCIAL_WIDGETS.MENU.DRAG')"
      />
      <h3
        :title="widget.title"
        class="flex-1 min-w-0 m-0 text-sm font-medium truncate text-n-slate-12"
      >
        {{ widget.title }}
      </h3>
      <span
        v-tooltip="tooltip"
        class="mt-0.5 i-lucide-info size-3.5 shrink-0 text-n-slate-10"
      />
      <div
        v-on-click-outside="() => (isMenuOpen = false)"
        data-pdf-ignore
        class="relative shrink-0"
      >
        <Button
          xs
          ghost
          slate
          icon="i-lucide-ellipsis"
          :title="t('COMMERCIAL_WIDGETS.MENU.LABEL')"
          @click="isMenuOpen = !isMenuOpen"
        />
        <DropdownMenu
          v-if="isMenuOpen"
          :menu-items="menuItems"
          class="mt-1 w-40 top-full ltr:right-0 rtl:left-0"
          @action="onMenuAction"
        />
      </div>
    </header>

    <div
      v-if="hasError"
      class="flex flex-col items-start flex-1 min-h-0 gap-1 overflow-auto text-sm text-n-ruby-9"
    >
      {{ t('COMMERCIAL_WIDGETS.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('COMMERCIAL_WIDGETS.RETRY') }}
      </button>
    </div>
    <p v-else-if="isLoading" class="m-0 text-sm animate-pulse text-n-slate-11">
      {{ t('COMMERCIAL_WIDGETS.LOADING') }}
    </p>
    <div
      v-else-if="isInvalid"
      class="flex flex-col items-start flex-1 min-h-0 gap-2 overflow-auto text-sm text-n-amber-11"
    >
      <span class="font-medium">
        {{ t('COMMERCIAL_WIDGETS.INVALID_REFERENCE.TITLE') }}
      </span>
      <span>{{ t('COMMERCIAL_WIDGETS.INVALID_REFERENCE.DESCRIPTION') }}</span>
      <Button
        xs
        faded
        slate
        :label="t('COMMERCIAL_WIDGETS.MENU.EDIT')"
        @click="emit('edit')"
      />
    </div>
    <div
      v-else-if="['number', 'rate'].includes(widget.visualization)"
      class="flex flex-col items-center justify-center flex-1 min-h-0 min-w-0 gap-1 overflow-hidden"
    >
      <span
        class="max-w-full text-3xl font-semibold truncate text-n-slate-12"
        :title="formatWidgetValue(data.value, data.format)"
      >
        {{ formatWidgetValue(data.value, data.format) }}
      </span>
      <span
        v-if="widget.visualization === 'rate'"
        class="text-xs text-n-slate-10"
      >
        {{
          t('COMMERCIAL_WIDGETS.RATE_OPERANDS', {
            numerator: formatWidgetValue(data.numerator, 'number'),
            denominator: formatWidgetValue(data.denominator, 'number'),
          })
        }}
      </span>
    </div>
    <WidgetCharts
      v-else-if="isChart"
      :visualization="widget.visualization"
      :data="data"
    />
    <WidgetTable v-else :data="data" />

    <span
      v-if="!isLoading && !isInvalid && data?.filtered_by_deals"
      v-tooltip="t('COMMERCIAL_WIDGETS.FILTERED_BY_DEALS.TOOLTIP')"
      class="self-start px-2 py-0.5 text-xs rounded-full shrink-0 bg-n-amber-3 text-n-amber-11"
    >
      {{ t('COMMERCIAL_WIDGETS.FILTERED_BY_DEALS.BADGE') }}
    </span>
  </article>
</template>
